// Client Hours Recording — Finance tab.
//
// Workflow:
//   1. Upload the Sage "Employee Segments" export (.xlsx).
//   2. Backend parses each per-job sheet.
//   3. User picks the target job/sheet, optionally adds Invoice # + Client.
//   4. Backend renders an ACS-branded PDF that faithfully reproduces the
//      sheet's data (including the full Notes column, which Excel's "Print
//      to PDF" was clipping in Carmela's prior workflow).
//
// Access: Finance role, Management, or FINANCIAL_ACCESS_IDS (Carmela = id 18).
// Server enforces the same gate.

import React, { useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Grid,
  MenuItem, Paper, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import api from '../services/api';

const NAVY = '#1A5490';
const GOLD = '#B8860B';

export default function ClientHoursRecording() {
  const fileInputRef = useRef(null);

  const [uploading, setUploading]     = useState(false);
  const [parsed, setParsed]           = useState(null);
  const [selectedSheet, setSelected]  = useState('');
  const [invoiceNo, setInvoiceNo]     = useState('');
  const [clientName, setClientName]   = useState('');
  const [generating, setGenerating]   = useState(false);
  const [snack, setSnack]             = useState({ open: false, msg: '', severity: 'success' });

  const pdfObjectUrlRef = useRef(null);
  const [pdfUrl, setPdfUrl] = useState('');

  const selectedSheetMeta = useMemo(() => {
    if (!parsed || !selectedSheet) return null;
    return parsed.sheets.find(s => s.sheet_name === selectedSheet) || null;
  }, [parsed, selectedSheet]);

  const showSnack = (msg, severity = 'success') =>
    setSnack({ open: true, msg, severity });

  const revokePdf = () => {
    if (pdfObjectUrlRef.current) {
      try { URL.revokeObjectURL(pdfObjectUrlRef.current); } catch (_) {}
      pdfObjectUrlRef.current = null;
    }
    setPdfUrl('');
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-uploading the same file
    if (!file) return;
    revokePdf();
    setParsed(null);
    setSelected('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('workbook', file);
      const { data } = await api.post('/client-hours-recording/parse', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setParsed(data);
      if (data.sheets?.length === 1) setSelected(data.sheets[0].sheet_name);
      showSnack(`Parsed ${data.sheets.length} job sheet${data.sheets.length === 1 ? '' : 's'}.`, 'success');
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Upload failed.';
      showSnack(msg, 'error');
    } finally {
      setUploading(false);
    }
  };

  const onGenerate = async () => {
    if (!parsed?.file_id || !selectedSheet) return;
    revokePdf();
    setGenerating(true);
    try {
      const { data } = await api.post(
        '/client-hours-recording/generate',
        {
          file_id: parsed.file_id,
          sheet_name: selectedSheet,
          invoice_no: invoiceNo || undefined,
          client_name: clientName || undefined,
        },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      pdfObjectUrlRef.current = url;
      setPdfUrl(url);
      showSnack('PDF ready. Preview + download below.', 'success');
    } catch (err) {
      // Blob-typed errors need special handling.
      let msg = 'Failed to generate PDF.';
      try {
        const blob = err?.response?.data;
        if (blob && blob.text) {
          const text = await blob.text();
          try { msg = JSON.parse(text).error || msg; } catch (_) { msg = text || msg; }
        } else if (err?.message) {
          msg = err.message;
        }
      } catch (_) {}
      showSnack(msg, 'error');
    } finally {
      setGenerating(false);
    }
  };

  const onDownload = () => {
    if (!pdfUrl || !selectedSheetMeta) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    const job = (selectedSheetMeta.job_number || 'JOB').replace(/[^\w-]+/g, '_');
    a.download = `ACS_Hours_Report_${job}_${selectedSheet.replace(/[^\w-]+/g, '_')}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const onReset = () => {
    revokePdf();
    setParsed(null);
    setSelected('');
    setInvoiceNo('');
    setClientName('');
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase',
            color: GOLD, fontWeight: 600, mb: 0.5,
          }}
        >
          Finance
        </Typography>
        <Typography variant="h4" sx={{ color: NAVY, fontWeight: 300, mb: 0.5 }}>
          Client Hours Recording
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Upload the Sage <em>Employee Segments</em> export, choose the job, and generate a
          client-facing PDF labor report. The output preserves every note in full — nothing gets
          clipped the way Excel's Print-to-PDF does.
        </Typography>
      </Box>

      {/* Cross-check reminder */}
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon />}
        sx={{ mb: 3, borderLeft: `4px solid ${NAVY}`, backgroundColor: '#f7f4ea' }}
      >
        Always cross-check the totals and any hand-entered notes against the raw Excel before sending
        to a client. The system is accurate but not infallible.
      </Alert>

      {/* Step 1 — upload */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ color: NAVY, mb: 1.5 }}>
          1. Upload Workbook
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
            onClick={onPickFile}
            disabled={uploading}
            sx={{ backgroundColor: NAVY, '&:hover': { backgroundColor: '#0d3560' } }}
          >
            {uploading ? 'Parsing…' : parsed ? 'Replace File' : 'Choose Excel File'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            style={{ display: 'none' }}
            onChange={onFileChange}
          />
          {parsed && (
            <>
              <Chip
                icon={<DescriptionIcon />}
                label={parsed.filename}
                sx={{ maxWidth: 380 }}
                onDelete={onReset}
              />
              <Typography variant="body2" color="text.secondary">
                {parsed.sheets.length} job sheet{parsed.sheets.length === 1 ? '' : 's'} detected
              </Typography>
            </>
          )}
        </Stack>
      </Paper>

      {/* Step 2 — pick sheet + optional metadata */}
      {parsed && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ color: NAVY, mb: 1.5 }}>
            2. Select Job &amp; Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Job / Sheet"
                value={selectedSheet}
                onChange={(e) => { revokePdf(); setSelected(e.target.value); }}
              >
                {parsed.sheets.map(s => (
                  <MenuItem key={s.sheet_name} value={s.sheet_name}>
                    {s.job_number ? `${s.job_number} — ` : ''}{s.job_name || s.sheet_name}
                    &nbsp;·&nbsp;{s.row_count} rows · {formatHrs(s.total_hours)} hrs
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Invoice # (optional)"
                value={invoiceNo}
                onChange={(e) => setInvoiceNo(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Client Name (optional)"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
              />
            </Grid>
            {selectedSheetMeta && (
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, backgroundColor: '#f0eee4', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    Preview
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>{selectedSheetMeta.job_number}</strong>
                    {selectedSheetMeta.job_name ? ` — ${selectedSheetMeta.job_name}` : ''}
                    {selectedSheetMeta.date_range ? ` · Period: ${selectedSheetMeta.date_range}` : ''}
                    &nbsp;·&nbsp;{selectedSheetMeta.row_count} rows
                    &nbsp;·&nbsp;<strong>{formatHrs(selectedSheetMeta.total_hours)}</strong> total hours
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Step 3 — generate */}
      {parsed && selectedSheet && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ color: NAVY, mb: 1.5 }}>
            3. Generate PDF
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdfIcon />}
              onClick={onGenerate}
              disabled={generating}
              sx={{ backgroundColor: NAVY, '&:hover': { backgroundColor: '#0d3560' } }}
            >
              {generating ? 'Generating…' : 'Generate PDF'}
            </Button>
            {pdfUrl && (
              <Button variant="outlined" onClick={onDownload} startIcon={<PictureAsPdfIcon />}>
                Download
              </Button>
            )}
          </Stack>

          {pdfUrl && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Preview
              </Typography>
              <Box sx={{ mt: 1, border: '1px solid #d5d5d5', borderRadius: 1, overflow: 'hidden', backgroundColor: '#f5f5f5' }}>
                <iframe
                  title="Client Hours Recording preview"
                  src={pdfUrl}
                  style={{ width: '100%', height: '75vh', border: 'none', display: 'block' }}
                />
              </Box>
            </>
          )}
        </Paper>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ maxWidth: 480 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function formatHrs(h) {
  const n = Number(h) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}
